import { useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { FormattedMessage, defineMessages, useIntl } from "react-intl";

import { useA11y } from "common/accessibility";
import { useDocumentTitles } from "util/document_title";
import { resetPassword, verifyResetPasswordToken } from "redux/actions/authentication";
import { UNAUTHORIZED_ERROR } from "errors/server";
import { useSelector, useDispatch } from "redux/util";
import { HookFormPassword } from "common/account/password/password";
import UnauthenticatedAppFrame from "common/app_frame/unauthenticated";
import { isPasswordStrong } from "util/password";
import { useForm } from "common/core/form";
import { defaultRequiredMessage } from "common/core/form/error";

import Styles from "./index.module.scss";
import {
  Background,
  ForgotPasswordHeader,
  BackToLoginLink,
  BackToLoginButton,
  TokenInvalid,
  ResetPasswordSubmitButton,
} from "./components";

const MESSAGES = defineMessages({
  passwordLabel: {
    id: "2b4ee807-7896-4cb4-a10e-ebb00f9233c8",
    defaultMessage: "Password",
  },
  passwordStrengthErrorLabel: {
    id: "8bcadcb3-bb45-403d-b650-6acc7d3ea619",
    defaultMessage: "Password not strong enough",
  },
});

export type FormValues = {
  password: string;
};

export default function DeprecatedResetPasswordPage() {
  const intl = useIntl();
  const { useDocumentEntitler } = useA11y();
  const token = useParams().token!;
  const query = new URLSearchParams(useLocation().search);
  const dispatch = useDispatch();
  const authentication = useSelector((state) => state.authentication);
  const { pending, completed, error } = authentication;

  const authError = !Object.keys(error).length ? null : error;
  const tokenInvalid = authError?.type === UNAUTHORIZED_ERROR;
  useDocumentEntitler({
    priority: "page",
    title: intl.formatMessage(useDocumentTitles().resetPassword),
  });

  useEffect(() => {
    dispatch(verifyResetPasswordToken(token));
  }, []);

  const onResetPassword = ({ password }: FormValues) => {
    dispatch(resetPassword(password, password, token));
  };

  const { handleSubmit, register, formState, setFocus } = useForm<FormValues>({
    mode: "all",
    defaultValues: {
      password: "",
    },
  });

  const { errors, isValid, isSubmitting, isDirty } = formState;

  return (
    <UnauthenticatedAppFrame>
      <div className={Styles.loginPage}>
        <div className={Styles.loginContent}>
          <div className={Styles.loginBackground}>
            <Background />
          </div>
          <div className={Styles.login}>
            {tokenInvalid && !completed ? (
              <TokenInvalid />
            ) : (
              !completed &&
              !pending && (
                <>
                  <ForgotPasswordHeader />
                  <form className={Styles.form} onSubmit={handleSubmit(onResetPassword)}>
                    <HookFormPassword
                      label={intl.formatMessage(MESSAGES.passwordLabel)}
                      withRequirements
                      setFocus={setFocus}
                      invalid={errors.password && isDirty}
                      registerProps={{
                        ...register("password", {
                          required: defaultRequiredMessage(intl),
                          validate: {
                            isPasswordStrong: (password) =>
                              isPasswordStrong(password) ||
                              intl.formatMessage(MESSAGES.passwordStrengthErrorLabel),
                          },
                        }),
                      }}
                    />
                    <ResetPasswordSubmitButton invalid={!isValid} submitting={isSubmitting} />
                  </form>
                  <BackToLoginLink />
                </>
              )
            )}

            {completed && (
              <>
                <p className={Styles.p}>
                  <FormattedMessage
                    id="483f3bb7-9a12-4bad-b4b6-9322ff381843"
                    defaultMessage="You have successfully updated your password!"
                  />
                </p>
                <BackToLoginButton redirect={query.get("redirect") || undefined} />
              </>
            )}
          </div>
        </div>
      </div>
    </UnauthenticatedAppFrame>
  );
}
